import React, { useEffect, useState } from 'react';
import Hero from './components/Hero/Hero';
import VideoSection from './components/VideoSection/VideoSection';
import ProgramSection from './components/ProgramSection/ProgramSection';
import MeetingsSection from './components/MeetingsSection/MeetingsSection';
import TestimonialSection from './components/TestimonialSection/TestimonialSection';
import './HomePage.scss';
import { Helmet } from 'react-helmet-async';
import EbookAd from './components/EbookAd/EbookAd';
import Newsletter from './components/Newsletter/Newsletter';
import { baseStrapiURL, baseURL } from '../../services/config';
import { useContentQuery } from '../../services/strapi/hooks';
import Loading from '../../components/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import Error404Page from '../Error404Page/Error404Page';
import StrapiImageWithSkeleton from '../../components/StrapiImageWithSkeleton/StrapiImageWithSkeleton';
import { useMediaQuery } from 'react-responsive';

const HomePage = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const strapiPage = 'home';

    const queryClient = useQueryClient();

    // Utilisation du hook personnalisé pour récupérer les données
    const { data: strapiData, isLoading, error, isFreshnessFetching } = useContentQuery(strapiPage);

    // Obtenir les données mises en cache ou les données actuelles
    const displayData = strapiData || queryClient.getQueryData(['strapi-content', strapiPage]);

    // N'afficher le chargement que si les données ne sont pas disponibles
    const showLoading = isLoading && !displayData;

    const [backgroundImage, setBackgroundImage] = useState('');

    // Effet pour mettre à jour l'image de fond lorsque les données sont chargées
    useEffect(() => {
        if (displayData && displayData?.PhraseAccroche_section && displayData?.PhraseAccroche_section.Photo) {
            setBackgroundImage(
                baseStrapiURL +
                displayData?.PhraseAccroche_section.Photo.url
            );

            // Vérifier le support WebP
            const checkWebPSupport = () => {
                const img = new Image();
                img.onload = () => {
                    if (img.height > 0 && img.width > 0) {
                        setBackgroundImage(
                            baseStrapiURL +
                            displayData?.PhraseAccroche_section.Photo.url
                        );
                    }
                };
                img.src = baseStrapiURL +
                    displayData?.PhraseAccroche_section.Photo.url;
            };

            checkWebPSupport();
        }
    }, [displayData]);

    useEffect(() => {
        window.scroll(0, 0);
    }, [])

    useEffect(() => {
        const checkWebPSupport = () => {
            const img = new Image();
            img.onload = () => {
                if (img.height > 0 && img.width > 0) {
                    setBackgroundImage(baseStrapiURL + displayData?.PhraseAccroche_section.Photo.url);
                }
            };

        };
        checkWebPSupport();
    }, []);

    const [isDelayedLoading, setIsDelayedLoading] = useState(false);

    // Gestion simple et robuste du loading
    useEffect(() => {
        // Si chargement ou vérification en cours, activer immédiatement
        if (isLoading || isFreshnessFetching) {
            setIsDelayedLoading(true);
            return;
        }

        // Sinon, désactiver après un court délai
        const timer = setTimeout(() => {
            setIsDelayedLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [isLoading, isFreshnessFetching]);

    if (isDelayedLoading) return <Loading />;
    if (!displayData || displayData?.length === 0 || error) return <Error404Page />;

    return (
        <div className="home-page">
            {
                displayData?.SEO &&
                    <Helmet>
                        {/* Open Graph (utilisé par Facebook, LinkedIn, etc.) */}
                        <meta property="og:title" content={displayData?.SEO.TitreOnglet} />
                        <meta property="og:description" content={displayData?.SEO.Description} />
                        <meta property="og:image" content={baseStrapiURL + displayData?.SEO.PhotoReseauxSociaux_1200x630.url} />
                        <meta property="og:url" content={displayData?.Slug === null ? baseURL : baseURL + '/' + displayData?.Slug} />
                        <meta property="og:type" content="website" />

                        {/* Twitter Cards */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content={displayData?.SEO.TitreOnglet} />
                        <meta name="twitter:description" content={displayData?.SEO.Description} />
                        <meta name="twitter:image" content={baseStrapiURL + displayData?.SEO.PhotoReseauxSociaux_1200x630.url} />
                        <meta name="twitter:url" content={displayData?.Slug === null ? baseURL : baseURL + '/' + displayData?.Slug} />

                        <title>{displayData?.SEO.TitreOnglet}</title>
                        <meta name="description" content={displayData?.SEO.Description} />
                        <link
                            rel="preload"
                            as="image"
                            href={baseStrapiURL + displayData?.Logo_section.Logo.url}
                        />
                        <link
                            rel="preload"
                            as="image"
                            href="/assets/main_rect_bkg.jpg"
                            fetchPriority="high"
                        />
                        <link
                            rel="preload"
                            href="/fonts/Great-Vibes.woff2"
                            as="font"
                            type="font/woff2"
                            crossOrigin="anonymous"
                        />
                    </Helmet>
            }
            <div
                className="home-page__background"
                aria-label={displayData?.PhraseAccroche_section.Photo.alternativeText}
                style={{ backgroundImage: `url(${backgroundImage})` }}
            ></div>
            <div className="logo_container">
                <StrapiImageWithSkeleton
                    picture={strapiData?.Logo_section.Logo}
                    className="logo"
                    loading="eager"
                    fetchpriority="high"
                />
            </div>
            {
                displayData?.PhraseAccroche_section &&
                <Hero strapiData={displayData?.PhraseAccroche_section} />
            }
            {
                displayData?.YoutubeVideo_section &&
                <VideoSection strapiData={displayData?.YoutubeVideo_section} />
            }
            {
                displayData?.AtelierMisEnAvant_section &&
                <ProgramSection strapiData={displayData?.AtelierMisEnAvant_section} />
            }
            {
                displayData?.ProchainesRencontres_section &&
                <MeetingsSection strapiData={displayData?.ProchainesRencontres_section} />
            }
            {
                displayData?.Ebook_section &&
                <EbookAd strapiData={displayData?.Ebook_section} />
            }
            {
                displayData?.Newsletter_section &&
                <Newsletter strapiData={displayData?.Newsletter_section} direction={isMobile ? 'text to picture' : 'picture to text'}/>
            }
            {
                displayData?.Avis_section &&
                <TestimonialSection strapiData={displayData?.Avis_section} />
            }
        </div>
    );
};

export default HomePage;